import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Building2, Search, Loader2, CheckCircle, XCircle, Eye, Plane, Hotel,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FALLBACK_TRIPS } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function TravellingAdminTravelDesk() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    flight_booked: false, flight_details: "",
    accommodation_booked: false, accommodation_details: "",
    other_notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: "pending_travel_desk" };
      if (search) params.search = search;
      const res = await axios.get(`${API}/travelling/requests`, { params, headers: getHeaders() });
      const fallbackPending = FALLBACK_TRIPS.filter(t => t.status === "pending_travel_desk" || t.status === "travel_desk_booked");
      setPending(res.data.length > 0 ? res.data : fallbackPending);
    } catch {
      const fallbackPending = FALLBACK_TRIPS.filter(t => t.status === "pending_travel_desk" || t.status === "travel_desk_booked");
      setPending(fallbackPending);
      toast.error("Failed to load. Showing demo data.");
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const openBooking = (trip) => {
    setSelected(trip);
    setBookingForm({
      flight_booked: trip.requires_flight || false,
      flight_details: "",
      accommodation_booked: trip.requires_accommodation || false,
      accommodation_details: "",
      other_notes: "",
    });
    setBookingOpen(true);
  };

  const handleBook = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/travelling/bookings`, {
        request_id: selected.id,
        ...bookingForm,
      }, { headers: getHeaders() });
      toast.success("Booking confirmed — trip forwarded to employee");
      setBookingOpen(false);
      fetchPending();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">TRAVEL DESK</h1>
            <p className="text-slate-600">Flight & accommodation booking management</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400" />
        <Input placeholder="Search pending requests..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-64 bg-app border-slate-300 rounded-none" />
        <Button onClick={fetchPending} size="sm" className="rounded-none bg-sky-600 hover:bg-sky-500"><Search className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : pending.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No pending travel desk requests.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-app border-b border-slate-200">
              <tr>
                {["Employee", "Destination", "Type", "Period", "Requires", "Actions"].map((h) => (
                  <th key={h} className="text-left p-3 font-semibold text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="p-3">
                    <p className="font-medium text-slate-800">{t.employee_name}</p>
                    <p className="text-xs text-slate-400">{t.employee_department}</p>
                  </td>
                  <td className="p-3 font-medium text-slate-700">{t.destination}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 ${t.trip_type === "foreign" ? "text-blue-600 bg-blue-50" : "text-slate-600 bg-slate-100"}`}>{t.trip_type}</span></td>
                  <td className="p-3 text-xs text-slate-500">{t.start_date?.slice(0,10)} → {t.end_date?.slice(0,10)}</td>
                  <td className="p-3">
                    <div className="flex gap-2 text-xs">
                      {t.requires_flight && <span className="flex items-center gap-1 text-blue-600"><Plane className="w-3 h-3" /> Flight</span>}
                      {t.requires_accommodation && <span className="flex items-center gap-1 text-purple-600"><Hotel className="w-3 h-3" /> Hotel</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    <Button size="sm" onClick={() => openBooking(t)} className="rounded-none bg-sky-600 hover:bg-sky-500 text-white">
                      <CheckCircle className="w-4 h-4 mr-1" /> Confirm Booking
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Confirm Booking — {selected?.destination}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 mt-2">
              <p className="text-sm text-slate-600">
                Employee: <strong>{selected.employee_name}</strong> | {selected.start_date?.slice(0,10)} → {selected.end_date?.slice(0,10)}
              </p>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={bookingForm.flight_booked} onChange={(e) => setBookingForm(f => ({ ...f, flight_booked: e.target.checked }))} />
                Flight Booked
              </label>
              <textarea placeholder="Flight details (carrier, flight #, time)" value={bookingForm.flight_details} onChange={(e) => setBookingForm(f => ({ ...f, flight_details: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={2} disabled={!bookingForm.flight_booked} />
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={bookingForm.accommodation_booked} onChange={(e) => setBookingForm(f => ({ ...f, accommodation_booked: e.target.checked }))} />
                Accommodation Booked
              </label>
              <textarea placeholder="Accommodation details (hotel, address, check-in/out)" value={bookingForm.accommodation_details} onChange={(e) => setBookingForm(f => ({ ...f, accommodation_details: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={2} disabled={!bookingForm.accommodation_booked} />
              <textarea placeholder="Other notes" value={bookingForm.other_notes} onChange={(e) => setBookingForm(f => ({ ...f, other_notes: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={2} />
              <Button onClick={handleBook} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-500 text-white rounded-none">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirming...</> : "Confirm & Send to Employee"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
